import type { BetslipState } from "sections/Betting/Betslip/betslipSlice";
import type { Context } from "../models/context";
import set from "lodash/set";
import type { TokenAllocation } from "hooks/firestore/v2/models/tokenAllocation";
import type { TokenAutomation } from "hooks/firestore/v2/models/tokenAutomation";
import { setIncludes } from "../setIncludes";
import type { TimesAllowedToCompleteFilterKey } from "hooks/firestore/v2/models/baseAutomation";

function isSelectionUsageTracked(
  selection: Context["selection"],
  automation: Context["automation"],
): boolean {
  const filterConfigHandlers = new Map<
    TimesAllowedToCompleteFilterKey,
    Array<string>
  >([
    ["eventIds", selection.eventIds],
    ["marketIds", selection.marketIds],
    ["marketTypeNames", selection.marketTypes],
    ["sports", selection.sports],
    ["meetingIds", selection.meetingIds],
    ["tournamentIds", selection.tournamentIds],
    ["seasonIds", selection.seasonIds ?? []],
  ]);

  const requirement = automation.requirements.find(
    (requirement) => !!requirement.userDrivenAttributes,
  );

  const filterList =
    requirement?.userDrivenAttributes?.timesAllowedToCompletePerEventFilter ??
    [];

  return filterList.some((filter) => {
    const valuesFromSelection = new Set(filterConfigHandlers.get(filter));
    const valuesFromConfiguration = new Set(
      requirement?.userDrivenAttributes?.[filter],
    );

    return valuesFromSelection
      ? setIncludes(valuesFromConfiguration, valuesFromSelection)
      : true;
  });
}

const betslipToUsagesContext = (
  betslip: BetslipState,
  allocations: TokenAllocation[] = [],
  automations: TokenAutomation[] = [],
  selectionContext: Context["selection"],
): Context["usages"] => {
  const selections = Object.values(betslip.selections);

  return selections.reduce((usages: Context["usages"], selection) => {
    const allocation = allocations?.find(
      (allocation) => allocation.id === selection.allocationId,
    );

    if (!allocation) return usages;

    const automation = automations?.find(
      (automation) => automation.automationId === allocation.automationId,
    );

    if (!automation) return usages;

    const requirement = automation.requirements.find(
      (requirement) => !!requirement.userDrivenAttributes,
    );

    const existingAllocations =
      usages[automation.automationId]?.[selection.eventId] || [];

    const filterList =
      requirement?.userDrivenAttributes?.timesAllowedToCompletePerEventFilter;

    if (!filterList) return usages;

    if (isSelectionUsageTracked(selectionContext, automation)) {
      set(usages, `${automation.automationId}.${selection.eventId}`, [
        ...existingAllocations,
        allocation.id,
      ]);
    }

    return usages;
  }, {});
};

export { betslipToUsagesContext };
